/**
 * RECOMMEND / EDIT RECOMMENDATION — the desktop send modal (DESIGN-CHANGELOG
 * 2026-09-03 "Sending (second pass)"; `design/GT Mine.dc.html:476-528`, view
 * model 699-716). AA phase 3.
 *
 * 520px. The game block (header art · name · developer), the TO row with THE
 * WHOLE PARTY chip, a 2-up checkbox roster of the party minus you (each row
 * peeking at what they already have), the note — a bare textarea, 160 chars,
 * sentence case, no field label (the markup has none) — and a footer: the hint
 * on the left, CANCEL, then SEND ▸ / SEND TO N ▸ / SAVE ▸ / REVOKE ▸, dead
 * until both halves are set. Root-mounted so any screen can open it.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { sendModalVM } from '../../store/selectors.ts';
import { Modal } from '../components/Modal.tsx';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Check({ on }: { on: boolean }): JSX.Element {
  return (
    <span
      style={{
        width: 14,
        height: 14,
        flex: 'none',
        boxSizing: 'border-box',
        border: '1px solid ' + (on ? 'var(--acc)' : 'var(--gt-border-2)'),
        background: on ? 'var(--acc)' : 'transparent',
        display: 'grid',
        placeItems: 'center',
        fontFamily: MONO,
        fontSize: 9,
        color: 'var(--gt-ink)',
      }}
    >
      {on ? '✓' : ''}
    </span>
  );
}

export default function SendModal(): JSX.Element {
  const s = useQd();
  const v = sendModalVM(s);
  const art = useArt(v.id + 'w', v.hdrCands);

  return (
    <Modal
      onClose={() => s.closeSend()}
      width={520}
      label="Recommend modal"
      header={{ title: v.title, titleClr: 'var(--gt-text)', titleFs: '10px', note: 'THEY SEE THE NOTE, NOT THE PARTY' }}
    >
      {/* the game ---------------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: '1px solid var(--gt-line)' }}>
        <div style={{ position: 'relative', width: 132, height: 62, flex: 'none', background: v.fall, overflow: 'hidden', border: '1px solid var(--gt-line)' }}>
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={11} ini={v.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--gt-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.n}
          </span>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-faint)' }}>{v.dev}</span>
        </div>
      </div>

      {/* TO · THE WHOLE PARTY · the roster ------------------------------------ */}
      <div style={{ padding: '14px 18px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.11em', color: 'var(--gt-faint)' }}>TO</span>
          <div style={{ flex: 1 }} />
          <div
            onClick={() => s.sdAll()}
            className="d-hov-acc-bd"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: '1px solid ' + (v.allOn ? 'var(--acc)' : 'var(--gt-border-2)'),
              color: v.allOn ? 'var(--acc)' : 'var(--gt-text-5)',
              background: v.allOn ? 'var(--accSoft)' : 'transparent',
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '.07em',
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            <Check on={v.allOn} />
            THE WHOLE PARTY
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {v.rows.map((r) => (
            <div
              key={r.key}
              onClick={() => s.sdToggle(r.k)}
              className="d-hov-acc-bd"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                border: '1px solid ' + (r.on ? 'var(--acc)' : 'var(--gt-border)'),
                background: r.on ? 'var(--accSoft)' : 'var(--gt-card)',
                padding: '8px 10px',
                cursor: 'pointer',
                minHeight: 44,
                boxSizing: 'border-box',
              }}
            >
              <Check on={r.on} />
              <div
                style={{
                  position: 'relative',
                  width: 24,
                  height: 24,
                  flex: 'none',
                  background: r.hueBg,
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: PIXEL,
                  fontSize: 7,
                  color: 'var(--gt-ink)',
                  overflow: 'hidden',
                }}
              >
                {r.ini}
                {r.hasAv ? <div style={{ position: 'absolute', inset: 0, background: r.avBg }} /> : null}
              </div>
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.name}
                </span>
                {r.hasPeek ? (
                  <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: r.peekClr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.peek}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* the note — sentence case, a person talking ---------------------------- */}
      <div style={{ padding: '10px 18px 14px' }}>
        <textarea
          value={v.note}
          onChange={(e) => s.sdSetNote(e.target.value)}
          maxLength={v.noteMax}
          rows={3}
          placeholder={v.notePh}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            resize: 'none',
            background: 'var(--gt-field)',
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text)',
            fontFamily: 'var(--gt-font-sans)',
            fontSize: 13,
            lineHeight: 1.5,
            padding: '9px 10px',
            outline: 'none',
          }}
        />
      </div>

      {/* footer: hint · CANCEL · SEND ------------------------------------------ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 18px', borderTop: '1px solid var(--gt-line)', background: 'var(--gt-raised)' }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: v.isRevoke ? 'var(--acc2)' : 'var(--gt-faint)', flex: 1, minWidth: 0 }}>
          {v.hint}
        </span>
        <span
          onClick={() => s.closeSend()}
          style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.07em', color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)', padding: '9px 12px', cursor: 'pointer' }}
        >
          CANCEL
        </span>
        <span
          onClick={() => (v.canSend ? void s.sdSend() : undefined)}
          style={{
            fontFamily: MONO,
            fontSize: '9.5px',
            letterSpacing: '.07em',
            color: v.isRevoke && v.canSend ? 'var(--acc2)' : v.sendClr,
            border: '1px solid ' + (v.isRevoke && v.canSend ? 'var(--acc2)' : v.sendBd),
            background: v.isRevoke && v.canSend ? 'var(--acc2Soft)' : v.sendBg,
            padding: '9px 14px',
            cursor: v.canSend ? 'pointer' : 'not-allowed',
          }}
        >
          {v.sendL}
        </span>
      </div>
    </Modal>
  );
}
